// Shared readers for the optional per-surface color keys under
// `styles.components` (SOW VT-KID-2026-001). Every key is optional:
// when a key is absent the caller falls back to whatever it rendered
// before these keys existed, so unconfigured sessions are unchanged.
//
// Keys handled here:
//   button.backgroundColor / button.hoverBackgroundColor
//   faceCapture.glowColor / guidanceIconColor / guidanceIconBackground
//   docCapture.glowColor / reviewCheckColor
//   loading.accentColor / loading.animation ("on" | "off")
//   processing.iconColor / ringColor / dotColor / accentColor
//   result.iconColor / result.iconBackground

// Accept "#101010", "101010", "rgb(...)", "rgba(...)", "hsl(...)" or a
// CSS color keyword. Bare 3/4/6/8-digit hex gets its "#" restored
// (URL params are commonly sent without it, matching ?primaryColor=).
export function normalizeColor(raw) {
  if (raw == null) return null;
  const value = String(raw).trim();
  if (!value) return null;
  if (/^[0-9a-f]{3,8}$/i.test(value)) return `#${value}`;
  return value;
}

// Pull `styles.components` off a session payload (or undefined when
// the session has not loaded yet / the caller has no DataContext).
export function getComponentStyles(data) {
  const components = data?.styles?.components;
  return components && typeof components === "object" ? components : {};
}

// Read a first-paint style value from the verification link. The
// pre-session loader renders before get_session resolves, so
// dcams-create-session mirrors the loading keys onto the URL the same
// way it already does for ?primaryColor=.
export function readStyleParam(name) {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(name);
  return raw == null || raw === "" ? null : raw;
}

export function readColorParam(name) {
  return normalizeColor(readStyleParam(name));
}

// "off" (any case) disables; everything else, including absence,
// keeps the animation on so existing sessions are unchanged.
export function isAnimationOff(value) {
  return typeof value === "string" && value.trim().toLowerCase() === "off";
}

// Hover / active fills for a CTA background that is not part of the
// Mantine palette. Mantine's own filled button uses palette[shade+1]
// on hover; a customer-supplied hex has no palette, so derive by
// darkening (or, for a near-black CTA, lightening so the change is
// still visible). Returns null when Mantine's color helpers are
// unavailable (some Storybook mounts).
export function deriveButtonStates(theme, backgroundColor) {
  if (!backgroundColor || typeof theme?.fn?.darken !== "function") {
    return { hover: null, active: null };
  }
  try {
    const dark = isDarkColor(backgroundColor);
    const hover = dark
      ? theme.fn.lighten(backgroundColor, 0.12)
      : theme.fn.darken(backgroundColor, 0.08);
    const active = dark
      ? theme.fn.lighten(backgroundColor, 0.2)
      : theme.fn.darken(backgroundColor, 0.14);
    return { hover, active };
  } catch (e) {
    return { hover: null, active: null };
  }
}

// Rough luminance check on hex input; non-hex colors are treated as
// light so hover darkens (the pre-existing Mantine direction).
export function isDarkColor(color) {
  const m = /^#?([0-9a-f]{6})/i.exec(String(color).trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.35;
}

// Root styles for a filled Mantine Button when the customer sets
// `components.button.backgroundColor`. Returns {} when unset so the
// Mantine primary palette keeps driving the fill exactly as today.
// `theme.fn.hover` emits the same @media (hover) wrapper Mantine uses
// for its own filled variant, so the override has matching specificity
// on touch devices (where hover maps to :active).
export function ctaButtonRootStyles(theme, button) {
  const backgroundColor = normalizeColor(button?.backgroundColor);
  if (!backgroundColor) return {};
  const derived = deriveButtonStates(theme, backgroundColor);
  const hover = normalizeColor(button?.hoverBackgroundColor) || derived.hover;
  const active = derived.active || hover;
  const hoverStyles =
    typeof theme?.fn?.hover === "function"
      ? theme.fn.hover({ backgroundColor: hover || backgroundColor })
      : { "&:hover": { backgroundColor: hover || backgroundColor } };
  return {
    backgroundColor,
    borderColor: "transparent",
    ...hoverStyles,
    "&:active": { backgroundColor: active || backgroundColor },
    "&[data-loading]": { backgroundColor },
  };
}

// Processing screen: accentColor is shorthand for all three; a
// specific key wins over the shorthand; `fallback` is the legacy
// primary-derived accent.
export function resolveProcessingColors(processing, fallback) {
  const accent = normalizeColor(processing?.accentColor) || fallback;
  return {
    iconColor: normalizeColor(processing?.iconColor) || accent,
    ringColor: normalizeColor(processing?.ringColor) || accent,
    dotColor: normalizeColor(processing?.dotColor) || accent,
  };
}

// styles.components.button.secondaryColor: text/border color of the
// non-filled Mantine buttons (outline, subtle, light), which otherwise
// take the primary color. Returns {} when unset or for other variants.
export function secondaryButtonRootStyles(theme, variant, button) {
  const color = normalizeColor(button?.secondaryColor);
  if (!color || !["outline", "subtle", "light"].includes(variant)) return {};
  const hover = (bg) =>
    typeof theme?.fn?.hover === "function"
      ? theme.fn.hover({ backgroundColor: bg })
      : { "&:hover": { backgroundColor: bg } };
  if (variant === "outline") {
    return { color, borderColor: color, ...hover(tintOf(theme, color, 0.06)) };
  }
  if (variant === "light") {
    return {
      color,
      backgroundColor: tintOf(theme, color, 0.12),
      ...hover(tintOf(theme, color, 0.18)),
    };
  }
  return { color, ...hover(tintOf(theme, color, 0.08)) };
}

// styles.components.checkmark.color: one "done / ready" color for every
// check-mark and success indicator in the journey (ID intro guideline
// icons, review checks, success result mark, live face-capture checklist
// dots, the v2 ID "ready" state). A surface-specific key passed as
// `specific` wins over it. Returns null when neither is set so callers
// keep their existing default.
export function resolveCheckmarkColor(components, specific) {
  return (
    normalizeColor(specific) ||
    normalizeColor(components?.checkmark?.color) ||
    null
  );
}

// Translucent tint for a solid color (used for the review-check disc
// and the result chip background when only an icon color is given).
export function tintOf(theme, color, alpha) {
  if (!color) return null;
  if (typeof theme?.fn?.rgba === "function") {
    try {
      return theme.fn.rgba(color, alpha);
    } catch (e) {
      /* fall through */
    }
  }
  return color;
}
